import type { CSSProperties, ReactNode } from 'react';

import type { JSONContent } from '@tiptap/core';

import { type SerializableNode, type SerializerProps, SerializeTextLeaf, type TTextLeaf, useIsClient } from '@/index';

import { computeFont } from '../utils/fonts';
import { getElement } from './utils/getElement';

import type { RichTextElementProps } from './RichText.types';
import './RichText.css';

const serializeNode = (
  { node, createKey = (entry) => entry[1].join(','), children }: SerializerProps,
  path = [0], // internal path tracking
): ReactNode => {
  const key = createKey([node, path]);

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node as TTextLeaf} />;
  }

  const nodeChildren =
    // @ts-expect-error
    node.content?.map((n, i) =>
      serializeNode(
        {
          node: n as JSONContent & SerializableNode,
          createKey,
        },
        [...path, i],
      ),
    ) || [];

  return getElement({
    node: node as SerializableNode,
    key,
    children: [...nodeChildren, children],
  });
};

const RichTextElement = ({ element }: RichTextElementProps) => {
  const isClient = useIsClient();

  if (!element?.attrs?.tiptapState || !isClient) {
    return null;
  }

  return (
    <div
      className="w-full"
      style={
        {
          fontFamily: computeFont(element?.attrs?.fontFamily),
          '--rich-text-color': element?.attrs?.color,
          '--rich-text-link-color': element?.attrs?.linkColor,
          '--rich-text-text-align': element?.attrs?.textAlign,
        } as CSSProperties
      }
    >
      <div className="rich-text-element">{serializeNode({ node: element.attrs.tiptapState as SerializableNode })}</div>
    </div>
  );
};

export { RichTextElement };
